import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { matchesAPI, teamsAPI, newsAPI } from '../services/api';
import AdBanner from '../components/AdBanner';
import SEOHead from '../components/SEOHead';

const CockpitDashboard = () => {
  const { data: matches = [] } = useQuery({ queryKey: ['matches'], queryFn: () => matchesAPI.getAll().then((r) => r.data) });
  const { data: teams = [] } = useQuery({ queryKey: ['teams'], queryFn: () => teamsAPI.getAll().then((r) => r.data) });
  const { data: news = [] } = useQuery({ queryKey: ['news'], queryFn: () => newsAPI.getAll().then((r) => r.data) });

  const live = matches.filter((m) => m.status === 'live');
  const upcoming = matches.filter((m) => m.status === 'scheduled' || !m.status).slice(0, 5);

  const chartData = teams.slice(0, 10).map((t) => ({ name: t.name, rank: t.fifa_ranking || 50 }));

  return (
    <>
      <SEOHead
        title="Cockpit Dashboard - WorldCup Fan Planner"
        description="Live scores, rankings, and news snapshot for World Cup planning."
        noindex
      />
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Cockpit Dashboard</h1>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border dark:border-gray-700">
          <h3 className="font-bold text-lg mb-2">Live Matches</h3>
          {live.length === 0 ? <p className="text-gray-500 dark:text-gray-400">No live matches</p> : live.map((m) => (
            <div key={m.id} className="text-sm">{m.team1_name} {m.score_team1} - {m.score_team2} {m.team2_name}</div>
          ))}
        </div>
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border dark:border-gray-700">
          <h3 className="font-bold text-lg mb-2">Upcoming</h3>
          {upcoming.map((m) => (
            <div key={m.id} className="text-sm py-1">{m.team1_name} vs {m.team2_name} – {m.date ? new Date(m.date).toLocaleDateString() : 'TBD'}</div>
          ))}
        </div>
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border dark:border-gray-700">
          <h3 className="font-bold text-lg mb-2">Team Rankings</h3>
          <div className="space-y-1 text-sm">
            {teams.slice(0, 5).map((t) => <div key={t.id}>{t.name} #{t.fifa_ranking || '-'}</div>)}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 h-64">
          <h3 className="font-bold mb-4">FIFA Rankings (Top 10)</h3>
          <ResponsiveContainer width="100%" height="80%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 60 }}>
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={80} />
              <Tooltip />
              <Bar dataKey="rank" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border dark:border-gray-700">
          <h3 className="font-bold mb-4">News</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {news.slice(0, 5).map((n) => (
              <a key={n.id || n.title} href={n.url} target="_blank" rel="noopener noreferrer" className="block text-sm text-amber-600 dark:text-amber-400 hover:underline">{n.title}</a>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border dark:border-gray-700">
        <h3 className="font-bold mb-4">YouTube Highlights</h3>
        <div className="aspect-video max-w-2xl">
          <iframe title="FIFA Highlights" className="w-full h-full rounded" src="https://www.youtube.com/embed/dQw4w9WgXcQ" allowFullScreen />
        </div>
      </div>

      <AdBanner className="my-6" slot="cockpit" />
    </div>
    </>
  );
};

export default CockpitDashboard;
